import './PDFTable.css'
import React, { useState, useEffect, useContext } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  doc,
  setDoc,
} from 'firebase/firestore';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  listAll,
} from 'firebase/storage';
import { AuthContext } from '../../services/AuthContext';

const ENTRIES_COLLECTION = 'pdfEntries';
const ROLES_COLLECTION = 'userRoles';

function PdfTable({ name }) {
  const { user, firebaseUid, db } = useContext(AuthContext);

  const [entries, setEntries] = useState([]);
  const [authorizedUids, setAuthorizedUids] = useState([]);
  const [loadingAuth, setLoadingAuth] = useState(true);

  const [editingRowId, setEditingRowId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  /* ===== Form alta ===== */
  const [norma, setNorma] = useState('');
  const [accionCumplimiento, setAccionCumplimiento] = useState('');
  const [estadoGestion, setEstadoGestion] = useState('Informativo');
  const [vencimiento, setVencimiento] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [hallazgoAuditoria, setHallazgoAuditoria] = useState('');
  const [auditoria2025, setAuditoria2025] = useState('Informativo');

  const [selectedFile, setSelectedFile] = useState(null);
  const [existingFiles, setExistingFiles] = useState([]);
  const [useExisting, setUseExisting] = useState(false);
  const [selectedExistingFile, setSelectedExistingFile] = useState('');

  const folderName = window.location.pathname.split('/').pop() || name;

  const [customName, setCustomName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('pendiente');

  const storage = getStorage();

  /* ==========================
     ROLES
  ========================== */
  useEffect(() => {
    if (!db) return;

    const q = query(collection(db, ROLES_COLLECTION));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const uids = [];
      snapshot.forEach((docSnap) => {
        if (docSnap.data().canEditCNA === true) {
          uids.push(docSnap.id);
        }
      });
      setAuthorizedUids(uids);
      setLoadingAuth(false);
    });

    return unsubscribe;
  }, [db]);

  /* ==========================
     ENTRIES + STORAGE
  ========================== */
  useEffect(() => {
    if (!db || loadingAuth) return;

    const q = query(
      collection(db, ENTRIES_COLLECTION),
      where('folder', '==', folderName)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setEntries(list);
    });

    const folderRef = ref(storage, folderName);
    listAll(folderRef).then((res) =>
      setExistingFiles(res.items.map((i) => i.name))
    );

    return unsubscribe;
  }, [db, folderName, loadingAuth]);

  useEffect(() => {
    document.querySelectorAll('.PdfTable textarea').forEach((ta) => {
      ta.style.height = 'auto';
      ta.style.height = ta.scrollHeight + 'px';
    });
  }, [entries, editingRowId]);

  const autoResize = (e) => {
    const el = e.target;

    if (!isRowEditable(el.closest('tr')?.getAttribute('data-id'))) return;

    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  const isAuthorized = firebaseUid && authorizedUids.includes(firebaseUid);
  const isRowEditable = (id) => isAuthorized && editingRowId === id;

  /* ==========================
     CRUD
  ========================== */
  const handleAddEntry = async () => {
    if (!norma || !isAuthorized) return;

    let fileName;
    let downloadURL = '';

    if (useExisting && selectedExistingFile) {
      fileName = selectedExistingFile;
      downloadURL = await getDownloadURL(
        ref(storage, `${folderName}/${fileName}`)
      );
    } else if (selectedFile) {
      fileName = selectedFile.name;
      const fileRef = ref(storage, `${folderName}/${fileName}`);
      await uploadBytes(fileRef, selectedFile);
      downloadURL = await getDownloadURL(fileRef);
    }

    await addDoc(collection(db, ENTRIES_COLLECTION), {
      folder: folderName,
      norma,
      accionCumplimiento,
      estadoGestion,
      vencimiento,
      observaciones,
      hallazgoAuditoria,
      auditoria2025,
      fileName,
      downloadURL,
    });

    setShowAddForm(false);
    setNorma('');
    setAccionCumplimiento('');
    setEstadoGestion('Informativo');
    setVencimiento('');
    setObservaciones('');
    setHallazgoAuditoria('');
    setAuditoria2025('Informativo');
    setSelectedFile(null);
    setUseExisting(false);
    setSelectedExistingFile('');
  };

  const handleUpdate = async (id, field, value) => {
    if (!isAuthorized) return;
    await setDoc(doc(db, ENTRIES_COLLECTION, id), { [field]: value }, { merge: true });
  };

  const handleDownload = (url, name) => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = name || 'archivo.pdf';
    a.click();
  };

  if (loadingAuth) return <div>Cargando permisos...</div>;

  return (
    <div className="PdfTable">

      <table>
        <thead>
          <tr>
            <th style={{ width: '5rem' }}>NORMA</th>
            <th style={{ width: '12rem' }}>ACCIÓN DE CUMPLIMIENTO</th>
            <th style={{ width: '12rem' }}>ESTADO</th>
            <th style={{ width: '6rem' }}>VENCIMIENTO</th>
            <th style={{ width: '10rem' }}>OBSERVACIONES</th>
            <th style={{ width: '10rem' }}>HALLAZGO</th>
            <th style={{ width: '12rem' }}>AUDITORÍA</th>
            <th style={{ width: '5rem' }}>ACCIONES</th>
          </tr>
        </thead>

        <tbody>
          {entries.map((entry) => {
            const editable = isRowEditable(entry.id);

            return (
              <tr key={entry.id} className={editable ? 'editing' : ''}>
                <td>
                  <span onClick={() => handleDownload(entry.downloadURL, entry.norma)}>
                    {entry.norma || entry.fileName}
                  </span>
                </td>

                <td>
                  <textarea
                    value={entry.accionCumplimiento || ''}
                    readOnly={!editable}
                    onChange={(e) => {
                      handleUpdate(entry.id, 'accionCumplimiento', e.target.value);
                      autoResize(e);
                    }}
                    onInput={autoResize}
                  />
                </td>

                <td>
                  <select
                    value={entry.estadoGestion || 'Informativo'}
                    onChange={(e) =>
                      handleUpdate(entry.id, 'estadoGestion', e.target.value)
                    }
                    disabled={!editable}
                  >
                    <option value="No cumple">No cumple</option>
                    <option value="En trámite C/Pendiente">En trámite C/Pendiente</option>
                    <option value="En trámite S/Pendiente">En trámite S/Pendiente</option>
                    <option value="Se debe adecuar">Se debe adecuar</option>
                    <option value="Cumple">Cumple</option>
                    <option value="Informativo">Informativo</option>
                  </select>
                </td>

                <td>
                  <input
                    type="text"
                    value={entry.vencimiento || ''}
                    readOnly={!editable}
                    onChange={(e) =>
                      handleUpdate(entry.id, 'vencimiento', e.target.value)
                    }
                  />
                </td>

                <td>
                  <textarea
                    value={entry.observaciones || ''}
                    readOnly={!editable}
                    onChange={(e) => {
                      handleUpdate(entry.id, 'observaciones', e.target.value);
                      autoResize(e);
                    }}
                    onInput={autoResize}
                  />
                </td>

                <td>
                  <textarea
                    value={entry.hallazgoAuditoria || ''}
                    readOnly={!editable}
                    onChange={(e) => {
                      handleUpdate(entry.id, 'hallazgoAuditoria', e.target.value);
                      autoResize(e);
                    }}
                    onInput={autoResize}
                  />
                </td>

                <td>
                  <select
                    value={entry.auditoria2025 || 'Informativo'}
                    onChange={(e) =>
                      handleUpdate(entry.id, 'auditoria2025', e.target.value)
                    }
                    disabled={!editable}
                  >
                    <option value="No cumple">No cumple</option>
                    <option value="En trámite C/Pendiente">En trámite C/Pendiente</option>
                    <option value="En trámite S/Pendiente">En trámite S/Pendiente</option>
                    <option value="Se debe adecuar">Se debe adecuar</option>
                    <option value="Cumple">Cumple</option>
                    <option value="Informativo">Informativo</option>
                  </select>
                </td>

                <td>
                  {isAuthorized && (
                    <button
                      onClick={() =>
                        setEditingRowId(editingRowId === entry.id ? null : entry.id)
                      }
                    >
                      {editable ? 'Bloquear' : 'Editar'}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {isAuthorized && (
        <button className="form-button" onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? 'Cancelar' : 'Agregar Entrada'}
        </button>
      )}

      {showAddForm && isAuthorized && (
        <div className="add-form">
          <input
            type="text"
            value={norma}
            onChange={(e) => setNorma(e.target.value)}
            placeholder="NORMA (clickeable)"
          />

          <textarea
            value={accionCumplimiento}
            onChange={(e) => setAccionCumplimiento(e.target.value)}
            placeholder="Acción de Cumplimiento"
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
          />

          <select
            value={estadoGestion}
            onChange={(e) => setEstadoGestion(e.target.value)}
          >
            <option value="No cumple">No cumple</option>
            <option value="En trámite C/Pendiente">En trámite C/Pendiente</option>
            <option value="En trámite S/Pendiente">En trámite S/Pendiente</option>
            <option value="Se debe adecuar">Se debe adecuar</option>
            <option value="Cumple">Cumple</option>
            <option value="Informativo">Informativo</option>
          </select>

          <input
            type="text"
            value={vencimiento}
            onChange={(e) => setVencimiento(e.target.value)}
            placeholder="Vencimiento"
          />

          <textarea
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            placeholder="Observaciones"
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
          />

          <textarea
            value={hallazgoAuditoria}
            onChange={(e) => setHallazgoAuditoria(e.target.value)}
            placeholder="Hallazgo de Auditoría"
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
          />

          <select
            value={auditoria2025}
            onChange={(e) => setAuditoria2025(e.target.value)}
          >
            <option value="No cumple">No cumple</option>
            <option value="En trámite C/Pendiente">En trámite C/Pendiente</option>
            <option value="En trámite S/Pendiente">En trámite S/Pendiente</option>
            <option value="Se debe adecuar">Se debe adecuar</option>
            <option value="Cumple">Cumple</option>
            <option value="Informativo">Informativo</option>
          </select>

          <label>
            <input
              type="checkbox"
              checked={useExisting}
              onChange={(e) => setUseExisting(e.target.checked)}
            />
            Usar PDF existente
          </label>

          {useExisting ? (
            <select
              value={selectedExistingFile}
              onChange={(e) => setSelectedExistingFile(e.target.value)}
            >
              <option value="">Selecciona un PDF</option>
              {existingFiles.map((file) => (
                <option key={file} value={file}>
                  {file}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => setSelectedFile(e.target.files[0])}
            />
          )}

          <button onClick={handleAddEntry}>Guardar Entrada</button>
        </div>
      )}
    </div>
  );
}

export default PdfTable;
